import { useState, useEffect } from "react";
import { Linking } from "react-native";
import { usePostHog } from "posthog-js/react";
import { useAuth } from "../../../hooks/useAuth";
import { useAppConfig } from "../../../AppConfigProvider";
import { useNavigation } from "@react-navigation/native";
import { useAppState, actions } from "../../../contexts/AppStateContext";
import { useSignupMutation } from "../../../graphql/generated/graphql";
import SignupForm, { SignupFormData, signupSchema } from "../../../components/auth/SignupForm/SignupForm";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { RootStackParamList } from "../../../navigation/AppNavigator.web";
import Sentry from "../../../utils/sentry";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

export function calculateAge(birthdayISO: Date): number {
  if (!birthdayISO) return 0;

  const birthDate = new Date(birthdayISO);
  const currentDate = new Date();
  let age = currentDate.getFullYear() - birthDate.getFullYear();
  const m = currentDate.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && currentDate.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

function SignupFormContainer() {
  const posthog = usePostHog();
  const { login } = useAuth();
  const appConfig = useAppConfig();
  const isCompanionMode = appConfig.isCompanionMode;
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { dispatch } = useAppState();
  const [signupMutation] = useSignupMutation();

  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [passwordHidden, setPasswordHidden] = useState(true);
  const [confirmPasswordHidden, setConfirmPasswordHidden] = useState(true);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
      birthday: null,
      gender: "",
      hasAgreedToTerms: false,
      dailyEmailOptIn: true,
    },
    mode: "onBlur",
    reValidateMode: "onChange",
    criteriaMode: "all",
    shouldFocusError: true,
  });

  const logo = appConfig.logo;
  const termsOfServiceLink = appConfig.termsOfServiceLink;
  const hasEmailVerification = appConfig.hasEmailVerification;

  useEffect(() => {
    posthog?.capture("Signup Page Visited");
  }, []);

  const handleTermsPress = () => {
    Linking.openURL(termsOfServiceLink);
  };

  const handlePasswordToggle = () => {
    setPasswordHidden((prev) => !prev);
  };

  const handleConfirmPasswordToggle = () => {
    setConfirmPasswordHidden((prev) => !prev);
  };

  const onSubmit = async (data: SignupFormData) => {
    setErrorMessage("");

    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      setIsLoading(true);

      if (!data.birthday) {
        setErrorMessage("Birthday is required");
        setIsLoading(false);
        return;
      }

      const result = await signupMutation({
        variables: {
          email: data.email.toLowerCase(),
          name: data.name,
          password: data.password,
          gender: data.gender,
          birthday: new Date(data.birthday).toISOString(),
          dailyEmailOptIn: data.dailyEmailOptIn,
          language: "",
          timezone: timezone,
        },
      });

      if (result.data) {
        const age = calculateAge(data.birthday);

        posthog?.identify(result?.data.signup.user.id, {
          name: result?.data.signup.user.name,
          age: age,
          gender: data.gender,
        });
        posthog?.capture("user signup", {
          distinctId: result?.data.signup.user.id,
        });

        const accessToken = result.data.signup.accessToken;
        const refreshToken = result.data.signup.refreshToken;
        await login(accessToken, refreshToken);

        setErrorMessage("");

        dispatch({
          type: actions.SET_META,
          payload: { user: result.data.signup.user, email: data.email.toLowerCase() },
        });

        if (hasEmailVerification) {
          navigation.navigate("Verify Email");
        } else if (isCompanionMode) {
          navigation.navigate("Companion Home");
        }
      }

      setIsLoading(false);
    } catch (error) {
      Sentry.captureException(error);
      setErrorMessage(error.message);
      setIsLoading(false);
    }
  };

  const handleSignInPress = () => {
    navigation.navigate("Login");
  };

  return (
    <SignupForm
      control={control}
      errors={errors}
      logo={logo}
      errorMessage={errorMessage}
      isLoading={isLoading}
      onTermsPress={handleTermsPress}
      onSubmit={handleSubmit(onSubmit)}
      onSignInPress={handleSignInPress}
      passwordHidden={passwordHidden}
      confirmPasswordHidden={confirmPasswordHidden}
      onPasswordToggle={handlePasswordToggle}
      onConfirmPasswordToggle={handleConfirmPasswordToggle}
    />
  );
}

export default SignupFormContainer;
